import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { cx, css } from 'emotion';
import Flexbox from 'flexbox-react';
import { Button, Table, Divider, Tag, Tooltip, Popover } from 'antd';
import _ from 'lodash';
import 'react-image-gallery/styles/css/image-gallery.css';

import AbstractForm from 'components/page/process/AbstractForm';
import RunDataConsolidatorForm from 'components/core/forms/RunDataConsolidatorForm';
import { colorBlue, colorVeryLightGrey, colorRed, getAnchorStyle, popoverErrorStyle } from 'styles/base';
import ModalsService from 'services/ModalsService';
import ProcessService from 'services/ProcessService';
import Constants from 'utils/Constants';
import ProcessUtils from 'utils/ProcessUtils';
import MessageBox from 'components/core/MessageBox';
import LoadingSpinner from 'components/core/LoadingSpinner';
import DataConsolidatorPlotViewer from 'components/core/DataConsolidatorPlotViewer';
import DataConsolidatorLogViewer from 'components/core/DataConsolidatorLogViewer';
import InfoBoxPageHeader from 'components/core/InfoBoxPageHeader';
import SelectGeopspatialVariablesForm from 'components/core/forms/SelectGeospatialVariablesForm';
import ImportDodForm from 'components/core/forms/ImportDodForm';
import DataExplorerService from 'services/DataExplorerService';
import ProcessEditor from 'utils/ProcessEditor';

const baseUrl = process.env.REACT_APP_BASE_URL; // this set via webpack build during transpile

const container = css`
    display: flex;
    flex: 1;
    flex-direction: column;
    max-width: 800px;
`;

const standardModeContainer = css`
    padding: 10px 10px 10px 20px;
`;

const sectionTitle = css`
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 5px;
`;

const linkStyle = css`
    font-weight: 700;
    margin: 0 5px 0 5px;
    ${getAnchorStyle()}
`;

const spacer = css`
    margin-left: 5px;
    margin-top: 5px;
`;

/**
 * This form lets you run a vap (or ingest with variable retrieval) via the data consolidator and
 * interact with past runs.
 */
class RunCodeForm extends AbstractForm {

    constructor(props) {
        super(props);

        this.state = {
            currentRunDate: null,
            logFileDate: null,
            dqPlot: null,
            logFileRoute: null,
            logFileTitle: null,
            runs: null
        };
    }

    componentWillMount() {
        // Make sure supporting information is loaded in the background
        ProcessService.fetchDataConsolidatorRunsIfNeeded(this.props.processId);

    }

    componentDidMount() {
        this.intervalId = setInterval(this.poll, 3000);
    }

    componentWillUnmount() {
        clearInterval(this.intervalId);
    }

    static propTypes = {
        process: PropTypes.object,
        processErrors: PropTypes.object,
        dispatch: PropTypes.func,
        hasWritePermissions: PropTypes.bool,
        siteFacilities: PropTypes.array,
        dods: PropTypes.array,
        wizardMode: PropTypes.bool
    };

    poll = () => {
        ProcessService.fetchDataConsolidatorRunsIfNeeded(this.props.processId, true);
    };

    getTableColumns() {
        return [
            {
                title: 'Run Date',
                dataIndex: 'runDate',
                key: 'runDate',
                render: runDate => {
                    return this.getFormattedDate(runDate);
                }
            },
            {
                title: 'Run Details',
                dataIndex: 'details',
                key: 'details',
                render: this.renderDetailsColumn
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: this.renderStatusColumn
            },
            {
                title: 'Action',
                key: 'action',
                render: this.renderActionColumn
            }
        ];
    }

    getFormattedDate(dateString) {
        // Replace underscore with a space and remove milliseconds tag
        let formattedDate = dateString.replace(/_/g, ' ');
        formattedDate = formattedDate.split('.')[0];
        return formattedDate;
    }

    hasRunParameterErrors() {
        const errors = ProcessService.getMyState().runParametersById[this.props.process.name].errors;
        return Object.keys(errors).length > 0;
    }

    checkRunEnablement() {
        let canRun = true;
        let message = 'Click to run process in data consolidator mode.';

        if (!this.props.userLoggedIn) {
            canRun = false;
            message = 'Only logged in users can run the data consolidator.';

        } else if (!this.props.hasWritePermissions) {
            canRun = false;
            message = 'You must be the owner of the process to run the data consolidator.';
        }
        
        return {
            status: canRun,
            message
        };
    }

    setDefaultRunParameters(rangeStatus) {

        // First check if run parameters are in the redux store
        let parameters = ProcessService.getMyState().runParametersById[this.props.process.name];
        if (_.isEmpty(parameters)) {
            // If not, then load them from the process object (they are stored under ui_state.run_parameters)
            parameters = ProcessUtils.getRunParameters(this.props.process, rangeStatus);
        } else {
            parameters = parameters.parameters;
        }
        ProcessService.updateRunParameters(this.props.process.name, parameters);
    }

    getRangeStatus() {
        const coordinateSystemNames = ProcessUtils.getCoordinateSystemNames(this.props.process);
        const exploredDatastreams = DataExplorerService.getExploredDatastreams(this.props.process.name);
        const isRangeRelevant = ProcessUtils.isRelevantTransformationParameter(this.props.process, 'range',
            coordinateSystemNames, 'time', exploredDatastreams);

        let rangeStatus = 'irrelevant';

        if (isRangeRelevant) {
            let hasNullRange = true;
            coordinateSystemNames.forEach(coordinateSystemName => {
                const dim = ProcessUtils.getDimension(this.props.process, coordinateSystemName, 'time');
                if (dim) {
                    const range = ProcessUtils.getTransformationDefault(this.props.process, 'range', coordinateSystemName, 'time');
                    if (range !== undefined && range !== Constants.RANGE_DEFAULT) {
                        hasNullRange = false;
                    }
                }
            });

            // User has not specified a range value in the PCM, so they can override it in the conf files when running
            // the data consolidator.
            if (hasNullRange) {
                rangeStatus = 'overridable';
            } else {
                rangeStatus = 'not_overridable';
            }
        }
        return rangeStatus;
    }

    onRunDataConsolidator = () => {
        const rangeStatus = this.getRangeStatus();
        this.setDefaultRunParameters(rangeStatus);

        // First we need to check if the process has data plotter variables defined (alt/lat/lon)
        if (!ProcessUtils.hasGeospatialVariables(this.props.process)) {
            // Allow user to select the datastream from which these variables will be obtained
            const title = (
                <div>
                    <div>Geospatial variables <strong>alt, lat, and lon</strong> are required in order to plot results correctly.</div>
                    <div>If you'd like to add these variables, select the dataset from which to obtain them. If no dataset is selected, plots will not be generated.</div>
                </div>
            );

            const modalTitle = (
                <Flexbox flexDirection="column">
                    <h4>Select Geospatial Variables</h4>
                    <InfoBoxPageHeader message={title} />
                </Flexbox>
            );

            let formComponent = null;
            const formRefFunction = (component) => { formComponent = component; };
            const onOk = () => { // dialog ok handler
                const value = formComponent.getGeospatialDatasetValue().value;
                if (value) {
                    const editor = new ProcessEditor(this.props.process);
                    editor.addGeospatialVariables(value);
                    editor.commit();
                }

                this.openRunModal(rangeStatus);
                return true;
            };
            const form = <SelectGeopspatialVariablesForm processId={this.props.process.name} ref={formRefFunction}/>;

            ModalsService.openModal({
                id: 'select_plotter_variables',
                modalType: ModalsService.TYPE_CONFIRM,
                modalTitle,
                modalMessage: form,
                onOk
            });

        } else {
            this.openRunModal(rangeStatus);
        }
    };

    openRunModal(rangeStatus) {
        const onOk = () => { // dialog ok handler
            // Check for validation errors
            let ret = false;
            const hasProcessErrors = !(this.checkRunEnablement().status);
            const hasRunParameterErrors = this.hasRunParameterErrors();
            if (!hasProcessErrors && !hasRunParameterErrors) {
                ProcessService.saveAndRunProcess(this.props.process.name);
                ret = true;
            }
            return ret;
        };

        const form = (
            <RunDataConsolidatorForm
                processId = {this.props.process.name}
                siteFacilities = {this.props.siteFacilities}
                hasWritePermissions = {this.props.hasWritePermissions}
                rangeStatus = {rangeStatus}
            />
        );

        ModalsService.openModal({
            id: 'data_consolidator_run',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle: 'Run Data Consolidator',
            modalMessage: form,
            modalSize: 'large',
            padding: '0',
            okDelay: 300,
            onOk,
            className: 'scrollable'
        });
    }

    onLog = (runDate) => {
        this.setState({
            logFileDate: runDate,
            logFileRoute: 'log',
            logFileTitle: 'Process Log File',
        });
    };

    onNcDump = (runDate) => {
        this.setState({
            logFileDate: runDate,
            logFileRoute: 'ncdump',
            logFileTitle: 'NCDump File',
        });
    };

    onPlot = (runDate) => {
        this.setState({
            currentRunDate: runDate,
            dqPlot: false
        });
    };

    onDQPlot = (runDate) => {
        this.setState({
            currentRunDate: runDate,
            dqPlot: true
        });
    };

    onImportDod = (runDate) => {
        // For now, only let the user run this if the process defines a single output datastream
        // We can change this later if it becomes a problem.
        if (this.props.process.output_datastreams.length > 1) {
            ModalsService.openModal({
                id: 'importdod_failed',
                modalType: ModalsService.TYPE_ERROR,
                modalTitle: 'Import DOD Not Allowed',
                modalMessage: 'Since this process has more than one output datastream, importing a DOD from the output is not allowed at this time.'
            });
            return;
        }

        const dod = this.props.process.output_datastreams[0];
        const onOk = () => {
            ProcessService.importDataConsolidatorRunOutput(this.props.processId, runDate, dod);
        };

        const form = (
           <ImportDodForm
              processId = {this.props.process.name}
              runDate = {runDate}
              dod={dod}
           />
        );

        ModalsService.openModal({
            id: 'confirm_import_dod',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle: 'Confirm Import',
            modalMessage: form,
            okDelay: 300,
            onOk
        });

    };

    onDelete = (runDate) => {
        // Pop up confirmation dialog
        const deleteOk = () => {
            ProcessService.deleteDataConsolidatorRun(this.props.processId, runDate);
        };

        ModalsService.openModal({
            id: 'confirm_delete_rundir',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle: 'Confirm Delete',
            modalMessage: `Are you sure you want to delete run ${runDate}?`,
            autoFocus: true,
            onOk: deleteOk
        });
    };

    onCancel = (runDate) => {
        // Pop up confirmation dialog
        const cancelOk = () => {
            ProcessService.cancelDataConsolidatorRun(this.props.processId, runDate);
        };

        ModalsService.openModal({
            id: 'confirm_cancel_rundir',
            modalType: ModalsService.TYPE_CONFIRM,
            modalTitle: 'Confirm Cancel',
            modalMessage: `Are you sure you want to cancel the data consolidator run: ${runDate}?`,
            autoFocus: true,
            onOk: cancelOk
        });
    };

    onPlotViewerClosed = () => {
        this.setState({
            currentRunDate: null,
            dqPlot: null
        });
    };

    onTextViewerClosed = () => {
        this.setState({
            logFileDate: null,
            logFileRoute: null,
            logFileTitle: null,
        });
    };

    onChange = (e) => {
        this.setState({
            value: e.target.value,
        });
    };

    onPathChange = (e) => {
        this.setState({
            path: e.target.value
        });
    };

    renderTable() {
        let tableComponent = <div/>;
        const runs = this.props.runs;
        const tableStyleOverrides = css`
            .ant-table-tbody {
                font-size: 12px;
                background-color: white;
            }
            .ant-table-tbody>tr:hover:not(.ant-table-expanded-row)>td {
                background: #e6f7ff;
            }
            .ant-tag {
                cursor: initial;
            }
            .ant-table-thead > tr > th {
                font-weight: 700 !important;
                color: ${colorVeryLightGrey} !important;
                background-color: ${colorBlue};
            }
        `;

        if (!this.props.userLoggedIn) {
            tableComponent = <div>You must be logged in to view data consolidator results.</div>;

        } else if (!runs) {
            tableComponent =  <LoadingSpinner/>;

        } else if (runs === 'Error') {
            tableComponent = <div>An error occured loading past data consolidator runs.  See server for more details.</div>;

        } else {
            // Only render the table if it is not loading and doesn't have an error
            let processDirMessage = <div/>;
            if (runs.length > 0) {
                processDirMessage = (
                    <em style={{ margin: '0 0 20px 10px' }}>* All run directories can be found on the DMF under:
                        <strong style={{ marginLeft: '8px' }}>{runs[0].processDir}</strong>
                    </em>
                );
            }
            tableComponent = (
                <Flexbox flexDirection="column" className={tableStyleOverrides}>
                    {processDirMessage}
                    <Table rowKey="runDate" columns={this.getTableColumns()} dataSource={runs} pagination={false}/>
                </Flexbox>
            );
        }

        return tableComponent;
    }

    renderDetailsColumn = (details) => {

        const labelStyle = css`
                      color: ${colorBlue};
                      font-weight: 700;
                      width: 90px;
                    `;
        return (
            <Flexbox flexDirection="column">
                <Flexbox flexDirection="row">
                    <div className={labelStyle}>Start Date:</div>
                    <div>{this.getFormattedDate(details.startDate)}</div>
                </Flexbox>
                <Flexbox flexDirection="row">
                    <div className={labelStyle}>End Date:</div>
                    <div>{this.getFormattedDate(details.endDate)}</div>
                </Flexbox>
                <Flexbox flexDirection="row">
                    <div className={labelStyle}>Run Location:</div>
                    <div>{details.runLocation}</div>
                </Flexbox>
            </Flexbox>
        );

    };

    renderStatusColumn = (status) => {
        let failure = false;
        let text = status;
        let color = 'volcano';

        if (status === 'Success') {
            color = 'green';
        } else if (status === 'Running') {
            color = 'gold';
        } else if (status === 'Killed') {
            color = 'gray';
        } else {
            failure = true;
            text = 'Failure';
        }

        let component = <Tag color={color} key={status}>{text}</Tag>;
        if (failure) {
            component = (
                <Tooltip title={status}>
                    {component}
                </Tooltip>
            );
        }

        return component;
    };

    renderActionColumn = (rowData) => {
        let component = <div/>;
        const delStyle = css`
            color: ${colorRed};
        `;
        const faSmall = css`
            font-size:0.63em;
        `;
        const dqStyle = css`
            top: 7px;
            left: 5px;
            font-size: 7px;
            display: block;
            position: absolute;
            color: ${colorRed};
            background: white;
            font-weight: 900;
            font-family: arial;
        `;
        const dodDividerStyle = css`
            margin-left: 22px;
        `;

        if (rowData.status == 'Running') {
            // Cancel button
            const cancelFunc = this.onCancel.bind(this, rowData.runDate);
            component = (
               <span>
                   <a onClick={cancelFunc} title="Cancel the data_consolidator run" target="_blank">
                       <i className={cx('fa fa-square', delStyle)} aria-hidden="true"/>
                   </a>
               </span>
            );

        } else {
            const sessionId = localStorage.getItem('sessionId');
            const url = `${baseUrl}/processes/${this.props.process.name}/getrun/${rowData.runDate}?session_id=${sessionId}`;

            // Log button
            const logFunc = this.onLog.bind(this, rowData.runDate);
            const log = (
                <a onClick={logFunc} title="Open log file" target="_blank">
                    <i className="fa fa-file-text-o" aria-hidden="true"/>
                </a>
            );

            // NCDump button
            const ncdumpFunc = this.onNcDump.bind(this, rowData.runDate);
            const ncdump = (
                <a onClick={ncdumpFunc} title="Open ncdump file" target="_blank">
                    <i className="fa fa-file-code-o" aria-hidden="true"/>
                </a>
            );

            // Download button
            const download = (
                <a href={url} title="Download run directory" target="_blank">
                    <i className="fa fa-download" aria-hidden="true"/>
                </a>
            );

            // Delete button
            const delFunc = this.onDelete.bind(this, rowData.runDate);
            const del = (
                <a onClick={delFunc} title="Delete run directory" target="_blank">
                    <i className={cx('fa fa-times', delStyle)} aria-hidden="true"/>
                </a>
            );

            if (rowData.status === 'Success') {
                const plotFunc = this.onPlot.bind(this, rowData.runDate);
                const plotDQFunc = this.onDQPlot.bind(this, rowData.runDate);
                const importDodFunc = this.onImportDod.bind(this, rowData.runDate);
                const plot = (
                    <a onClick={plotFunc} title="Plot results">
                        <i className="fa fa-area-chart" aria-hidden="true"/>
                    </a>
                );
                const dqplot = (
                    <a onClick={plotDQFunc} title="Plot results in DQ Inspector" >
                        <span className="fa fa-area-chart" aria-hidden="true" style={{ position: 'relative' }}>
                            <span className={dqStyle}>DQ</span>
                        </span>
                    </a>
                );
                const importDod= (
                    <a onClick={importDodFunc} title="Import output to new DOD version">
                       <span className="fa fa-level-up" aria-hidden="true" style={{ position: 'relative' }}>
                         <span className={dqStyle} style={{ left: '7px' }}>DOD</span>
                        </span>
                    </a>
                );
                component = (
                    <span>
                        {log}
                        <Divider type="vertical"/>
                        {ncdump}
                        <Divider type="vertical"/>
                        {plot}
                        <Divider type="vertical"/>
                        {dqplot}
                        <Divider type="vertical"/>
                        {importDod}
                        <Divider type="vertical" className={dodDividerStyle}/>
                        {download}
                        <Divider type="vertical"/>
                        {del}
                    </span>
                );
            } else {
                component = (
                    <span>
                        {log}
                        <Divider type="vertical"/>
                        {download}
                        <Divider type="vertical"/>
                        {del}
                    </span>
                );
            }
        }

        return component;

    };

    renderLogFileViewer() {
        let comp = <div/>;
        if (this.state.logFileDate) {
            comp = (
                <DataConsolidatorLogViewer
                    onClose={this.onTextViewerClosed}
                    processName={this.props.process.name}
                    baseUrl={baseUrl}
                    title={this.state.logFileTitle}
                    runDate={this.state.logFileDate}
                    route={this.state.logFileRoute} />
            );
        }
        return comp;
    }

    renderImageGallery() {
        let comp = <div/>;
        if (this.state.currentRunDate) {
            comp = (
                <DataConsolidatorPlotViewer
                    onClose={this.onPlotViewerClosed}
                    process={this.props.process}
                    userLoggedIn={this.props.userLoggedIn}
                    dqPlot={this.state.dqPlot}
                    runDate={this.state.currentRunDate}/>
            );
        }
        return comp;
    }

    renderMessageBox() {
        return (
            <MessageBox includeIcon>
                <div>If you need to create new variables or modify input or output data with custom logic, you should create a code project instead.
                    <span className={spacer}>Click
                        <a href="https://engineering.arm.gov/ADI_doc/pcm.html#running-processes-defined-in-the-pcm"
                            className={linkStyle} target="_blank" rel="noopener noreferrer">
                            HERE
                        </a>
                    for more information.
                    </span>
                </div>
            </MessageBox>
        );
    }

    render() {
        const enablement = this.checkRunEnablement();
        const canRun = enablement.status;
        const title = enablement.message;
        const disabledClass = canRun ? '' : 'disabled';

        let runButtonComponent = (
            <Button onClick={this.onRunDataConsolidator} type="primary" disabled={disabledClass} title={title}>
                Run Now
            </Button>
        );

        if (!canRun) {
            runButtonComponent = (
                <Popover
                    content={enablement.message}
                    placement="top"
                    overlayClassName={popoverErrorStyle}
                >
                    {runButtonComponent}
                </Popover>
            );
        }

        let runContainer = container;
        if (!this.props.wizardMode) {
            runContainer = cx(runContainer, standardModeContainer);
        }

        return (
            <div className={runContainer}>
                {!this.props.wizardMode &&
                    <div className={sectionTitle}>Data Consolidator</div>
                }
                <Flexbox flexDirection="column">
                    <Flexbox flexDirection="row" alignItems="flex-start">
                        <div style={{ marginRight: '20px' }}>
                            <div style={{ marginBottom: '10px' }}>
                                Run the data consolidator to consolidate all the datastreams defined by this process
                                into a single output file.
                            </div>
                        </div>
                        {runButtonComponent}
                    </Flexbox>
                </Flexbox>

                <div className={cx(sectionTitle, css`margin-top:30px;`)}>Recent Runs</div>
                {this.renderTable()}
                {this.renderImageGallery()}
                {this.renderLogFileViewer()}

            </div>
        );
    }
}

// Automatically pull data from the redux store into props whenever the store changes
const mapStateToProps = (storeState, ownProps) => {
    const processId = ownProps.processId;
    const processState = ProcessService.getMyState(storeState);
    const runs = processState.dataConsolidatorRunsById[processId];

    return {
        runs
    };
};

export default connect(mapStateToProps)(RunCodeForm);


// WEBPACK FOOTER //
// ./src/components/page/process/RunCodeForm.js